import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Content, Page, Header } from '@backstage/core-components';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';

import { spassuCodeApiRef } from '../../api';
import type { PaginatedResult, SkillSummary } from '../../api/types';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(2),
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  filterSelect: {
    minWidth: 140,
  },
  table: {
    minWidth: 900,
  },
  chipActive: {
    backgroundColor: '#4CAF50',
    color: '#fff',
  },
  chipDraft: {
    backgroundColor: '#9E9E9E',
    color: '#fff',
  },
  chipReview: {
    backgroundColor: '#FF9800',
    color: '#fff',
  },
  chipCommunity: {
    backgroundColor: '#2196F3',
    color: '#fff',
  },
  chipDeprecated: {
    backgroundColor: '#F44336',
    color: '#fff',
  },
  signatureValid: {
    color: '#4CAF50',
  },
  signatureInvalid: {
    color: '#F44336',
  },
  signatureNone: {
    color: '#9E9E9E',
  },
  stats: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}));

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  pending_review: 'Em Review',
  approved: 'Aprovada',
  active: 'Ativa',
  deprecated: 'Deprecada',
  community_submitted: 'Comunidade',
};

function getStatusChipClass(
  status: string,
  classes: ReturnType<typeof useStyles>,
): string {
  switch (status) {
    case 'active':
      return classes.chipActive;
    case 'draft':
      return classes.chipDraft;
    case 'pending_review':
      return classes.chipReview;
    case 'community_submitted':
      return classes.chipCommunity;
    case 'deprecated':
      return classes.chipDeprecated;
    default:
      return classes.chipDraft;
  }
}

export const SkillManagementPage = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);

  const [skills, setSkills] = useState<PaginatedResult<SkillSummary>>({
    data: [],
    total: 0,
    page: 1,
    limit: 20,
  });
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [originFilter, setOriginFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSkill, setNewSkill] = useState({
    name: '',
    content: '',
    phase: '',
    description: '',
  });

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.listSkills({
        status: statusFilter || undefined,
        origin: originFilter || undefined,
      });
      setSkills(result);
    } catch {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter, originFilter]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const handleCreate = async () => {
    try {
      await api.createSkill(newSkill);
      setCreateOpen(false);
      setNewSkill({ name: '', content: '', phase: '', description: '' });
      fetchSkills();
    } catch {
      // Error handled silently
    }
  };

  const handleSign = async (id: string) => {
    await api.signSkill(id);
    fetchSkills();
  };

  const handleSubmitReview = async (id: string) => {
    try {
      await api.submitSkillForReview(id);
      fetchSkills();
    } catch {
      // Error — injection scan may have failed
    }
  };

  const handleApprove = async (id: string) => {
    await api.approveSkill(id);
    fetchSkills();
  };

  return (
    <Page themeId="tool">
      <Header
        title="Skill Vault"
        subtitle="Gerenciamento de skills corporativas assinadas"
      />
      <Content>
        <div className={classes.root}>
          <div className={classes.toolbar}>
            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterSelect}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as string)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="draft">Rascunho</MenuItem>
                <MenuItem value="pending_review">Em Review</MenuItem>
                <MenuItem value="active">Ativa</MenuItem>
                <MenuItem value="community_submitted">Comunidade</MenuItem>
                <MenuItem value="deprecated">Deprecada</MenuItem>
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              size="small"
              className={classes.filterSelect}
            >
              <InputLabel>Origem</InputLabel>
              <Select
                value={originFilter}
                onChange={e => setOriginFilter(e.target.value as string)}
                label="Origem"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="internal">Interna</MenuItem>
                <MenuItem value="community">Comunidade</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title="Atualizar">
              <IconButton onClick={fetchSkills} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              Nova Skill
            </Button>
          </div>

          <TableContainer>
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Fase</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Assinatura</TableCell>
                  <TableCell align="right">Usos</TableCell>
                  <TableCell align="right">Qualidade</TableCell>
                  <TableCell>Acoes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.data.map(skill => (
                  <TableRow key={skill.id}>
                    <TableCell>
                      <Typography variant="body2">{skill.name}</Typography>
                      {skill.description && (
                        <Typography variant="caption" color="textSecondary">
                          {skill.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{skill.phase ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[skill.status] ?? skill.status}
                        className={getStatusChipClass(skill.status, classes)}
                      />
                    </TableCell>
                    <TableCell>
                      {skill.origin === 'community' ? 'Comunidade' : 'Interna'}
                    </TableCell>
                    <TableCell>
                      {skill.signature ? (
                        <Tooltip
                          title={`Assinada por ${
                            skill.signedBy ?? 'desconhecido'
                          }`}
                        >
                          <CheckCircleIcon
                            className={classes.signatureValid}
                            fontSize="small"
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Sem assinatura">
                          <ErrorIcon
                            className={classes.signatureNone}
                            fontSize="small"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">{skill.usageCount}</TableCell>
                    <TableCell align="right">
                      {skill.avgQualityScore !== null
                        ? skill.avgQualityScore.toFixed(1)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className={classes.stats}>
                        {skill.status === 'draft' && (
                          <>
                            <Button
                              size="small"
                              onClick={() => handleSign(skill.id)}
                            >
                              Assinar
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleSubmitReview(skill.id)}
                            >
                              Review
                            </Button>
                          </>
                        )}
                        {skill.status === 'pending_review' && (
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => handleApprove(skill.id)}
                          >
                            Aprovar
                          </Button>
                        )}
                        {skill.status === 'community_submitted' && (
                          <Button
                            size="small"
                            onClick={() => handleSubmitReview(skill.id)}
                          >
                            Scan + Review
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {skills.data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {loading ? 'Carregando...' : 'Nenhuma skill encontrada'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Create Skill Dialog */}
        <Dialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Nova Skill</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Nome"
              value={newSkill.name}
              onChange={e => setNewSkill({ ...newSkill, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Descricao"
              value={newSkill.description}
              onChange={e =>
                setNewSkill({ ...newSkill, description: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Fase (opcional)"
              value={newSkill.phase}
              onChange={e =>
                setNewSkill({ ...newSkill, phase: e.target.value })
              }
              margin="normal"
              placeholder="gerando-prd, gerando-techspec, etc."
            />
            <TextField
              fullWidth
              label="Conteudo (Markdown)"
              value={newSkill.content}
              onChange={e =>
                setNewSkill({ ...newSkill, content: e.target.value })
              }
              margin="normal"
              multiline
              rows={12}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              color="primary"
              variant="contained"
              disabled={!newSkill.name || !newSkill.content}
            >
              Criar
            </Button>
          </DialogActions>
        </Dialog>
      </Content>
    </Page>
  );
};
